import clsx from 'clsx'
import { useCallback, useMemo } from 'react'
import { formatDateToPtBr, KTSVG, IconPaths } from '../../../../../../../_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  IndicadoresBarChart,
  InfoAlert,
  ListLoading,
  SerieIndicador,
} from '../../../../../../components'
import { MetricaIndicador } from '../../core/_models'
import { formatarContagem, formatarDuracao } from '../../core/formatacao'
import { AjudaIndicador, montarNotasAjuda } from '../ajuda/AjudaIndicador'
import { useParametrosIndicadores } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { IndicadorCard } from '../cards/IndicadorCard'

/**
 * Família operacional: os cinco indicadores do contrato congelado da D4a
 * (os dois cortes de lead time nunca somados), mais a faixa de totais que é o
 * denominador do denominador.
 */
export const SecaoOperacional = () => {
  const { t } = useT()
  const { parametros } = useParametrosIndicadores()
  const { singleResponse: dados, isLoading } = useQueryResponse()

  const serieGrafico = useMemo<SerieIndicador[]>(() => {
    if (!dados) return []

    const candidatos: { categoria: string; metrica: MetricaIndicador }[] = [
      {
        categoria: t('indicadores.operacional.lead_time_agendados'),
        metrica: dados.leadTimeAgendamento.agendados,
      },
      {
        categoria: t('indicadores.operacional.lead_time_demanda_imediata'),
        metrica: dados.leadTimeAgendamento.demandaImediata,
      },
      { categoria: t('indicadores.operacional.tempo_espera'), metrica: dados.tempoEspera },
      { categoria: t('indicadores.operacional.atraso_inicio'), metrica: dados.atrasoInicio },
      { categoria: t('indicadores.operacional.duracao'), metrica: dados.duracaoAtendimento },
    ]

    // Indicador sem dado não entra na série: o card ao lado diz "Sem dados no período".
    return candidatos
      .filter((c) => c.metrica.media !== null && c.metrica.mediana !== null)
      .map((c) => ({
        categoria: c.categoria,
        media: c.metrica.media as number,
        mediana: c.metrica.mediana as number,
      }))
  }, [dados, t])

  const unidadeMedida = dados?.unidadeMedida ?? ''
  const formatarValorGrafico = useCallback(
    (valor: number) => formatarDuracao(valor, unidadeMedida) ?? '',
    [unidadeMedida]
  )

  if (!parametros) {
    return (
      <InfoAlert variant='info' className='mb-0'>
        {t('indicadores.estado.selecione_empresa')}
      </InfoAlert>
    )
  }

  return (
    <div aria-live='polite' aria-busy={isLoading}>
      {isLoading && <ListLoading />}
      {isLoading && !dados && (
        <span className='visually-hidden'>{t('indicadores.estado.carregando')}</span>
      )}

      {dados && (
        <div className={clsx(isLoading && 'opacity-50')}>
          <p className='fs-7 text-gray-700 mb-5'>
            {t('indicadores.periodo', {
              inicio: formatDateToPtBr(dados.dataInicio),
              fim: formatDateToPtBr(dados.dataFim),
            })}
          </p>

          <div className='row g-5 mb-8'>
            <div className='col-xl-4 col-md-6'>
              <IndicadorCard
                titulo={t('indicadores.operacional.lead_time_agendados')}
                metrica={dados.leadTimeAgendamento.agendados}
                unidadeMedida={dados.unidadeMedida}
                notaMetodologica={dados.rotulos?.leadTimeAgendamento}
              />
            </div>
            <div className='col-xl-4 col-md-6'>
              <IndicadorCard
                titulo={t('indicadores.operacional.lead_time_demanda_imediata')}
                metrica={dados.leadTimeAgendamento.demandaImediata}
                unidadeMedida={dados.unidadeMedida}
                notaMetodologica={dados.rotulos?.leadTimeAgendamento}
              />
            </div>
            <div className='col-xl-4 col-md-6'>
              <IndicadorCard
                titulo={t('indicadores.operacional.tempo_espera')}
                metrica={dados.tempoEspera}
                unidadeMedida={dados.unidadeMedida}
                notaMetodologica={dados.rotulos?.tempoEspera}
                indisponivel={dados.tempoEspera.indisponivel}
              />
            </div>
            <div className='col-xl-4 col-md-6'>
              <IndicadorCard
                titulo={t('indicadores.operacional.atraso_inicio')}
                metrica={dados.atrasoInicio}
                unidadeMedida={dados.unidadeMedida}
                notaMetodologica={dados.rotulos?.inicioDuracao}
              />
            </div>
            <div className='col-xl-4 col-md-6'>
              <IndicadorCard
                titulo={t('indicadores.operacional.duracao')}
                metrica={dados.duracaoAtendimento}
                unidadeMedida={dados.unidadeMedida}
                notaMetodologica={dados.rotulos?.inicioDuracao}
              />
            </div>
          </div>

          <div className='card card-flush border border-gray-300 mb-8'>
            <div className='card-body p-6'>
              <div className='d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4'>
                <h3 className='fs-6 fw-semibold text-gray-700 mb-0'>
                  {t('indicadores.totais.titulo')}
                </h3>
                {/*
                  Qualidade de dado, não indicador: só ocupa espaço quando há o que
                  apontar. Zerado, some; a regra do cálculo fica no `?`.
                */}
                {dados.totalSemDataSolicitacao > 0 && (
                  <span className='badge badge-light-warning text-gray-900 fs-7 fw-semibold d-inline-flex align-items-center gap-1 py-1 ps-3 pe-1'>
                    <KTSVG
                      path={IconPaths.Warning}
                      className='svg-icon-em text-warning fs-6 me-1'
                      ariaHidden
                    />
                    {t('indicadores.totais.sem_data_badge', {
                      n: formatarContagem(dados.totalSemDataSolicitacao),
                    })}
                    <AjudaIndicador
                      rotulo={t('indicadores.ajuda.rotulo', {
                        titulo: t('indicadores.totais.sem_data_solicitacao'),
                      })}
                      notas={montarNotasAjuda('sem-data-solicitacao', [
                        dados.rotulos?.semDataSolicitacao,
                      ])}
                    />
                  </span>
                )}
              </div>
              <div className='row g-5'>
                <div className='col-md-6'>
                  <p className='fs-3 fw-bold text-gray-900 mb-1'>
                    {formatarContagem(dados.totalAtendimentosPeriodo)}
                  </p>
                  <p className='fs-8 text-gray-700 mb-0'>{t('indicadores.totais.atendimentos')}</p>
                </div>
                <div className='col-md-6'>
                  <p className='fs-3 fw-bold text-gray-900 mb-1'>
                    {formatarContagem(dados.totalExcluidosCancelamentoFalta)}
                  </p>
                  <p className='fs-8 text-gray-700 mb-0'>{t('indicadores.totais.excluidos')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className='card card-flush border border-gray-300'>
            <div className='card-body p-6'>
              <h3 className='fs-6 fw-semibold text-gray-700 mb-4'>
                {t('indicadores.grafico.titulo')}
              </h3>
              <IndicadoresBarChart
                dados={serieGrafico}
                rotuloMedia={t('indicadores.grafico.media')}
                rotuloMediana={t('indicadores.grafico.mediana')}
                textoSemDados={t('indicadores.card.sem_dados')}
                formatarValor={formatarValorGrafico}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
